import { RefObject, useLayoutEffect, useRef, useState } from 'react';

export const TruncationAxis = {
  // A single-line element, ellipsis-truncated horizontally (white-space:nowrap + text-overflow).
  HORIZONTAL: 'horizontal',
  // A multi-line, -webkit-line-clamp'd element, truncated vertically once it exceeds its clamped
  // line count.
  VERTICAL: 'vertical'
} as const;
export type TruncationAxisType = (typeof TruncationAxis)[keyof typeof TruncationAxis];

// Whether an element's text overflows its box (i.e. is ellipsis-truncated), on the given axis.
// Attach the returned ref to the truncating element; it re-measures whenever `text` changes. Use it
// to reveal the full text (e.g. a `title` attribute) only when it's actually cut off.
export const useIsTruncated = <T extends HTMLElement>(
  text: string,
  axis: TruncationAxisType = TruncationAxis.HORIZONTAL
): [RefObject<T | null>, boolean] => {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      // Ignore readings on a detached node: a transient 0×0 measurement (e.g. from a final resize
      // callback as the node unmounts) would otherwise wrongly clear a real truncation.
      if (el.isConnected) {
        setIsTruncated(
          axis === TruncationAxis.VERTICAL
            ? el.scrollHeight > el.clientHeight
            : el.scrollWidth > el.clientWidth
        );
      }
    };
    measure();
    // Re-measure on resize: the element's available size settles/changes after mount (async data,
    // responsive card grid), so a one-shot measure keyed only on `text` goes stale and truncation is
    // missed.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, axis]);

  return [ref, isTruncated];
};
